import { doodBlockConfig, propertyValue } from "./dood-block-config";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";

export const SHOP_AROUND_LIST_HEADER_LEGACY_FIELD_ORDER = [
  "title",
  "display_title",
  "display_address_search_input",
  "shop_search_location_field_background_color",
  "shop_search_location_field_text_color",
  "shop_search_location_field_placeholder_color",
  "shop_search_location_field_icon_color",
  { type: "separator" },
  "display_filters_button",
  "display_map_button",
  "display_map_button_on_mobile",
  "shops_map_button_color",
  "shops_map_button_text_color",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  shopAroundListHeaderBlockDescriptor: _generatedShopAroundListHeaderBlockDescriptor,
  shopAroundListHeaderBlockBlocDefinition,
  shopAroundListHeaderBlockDashboardLayout,
  shopAroundListHeaderBlockConfig,
  shopAroundListHeaderBlockResolveConfig,
  shopAroundListHeaderBlockProperties,
} = doodBlockConfig(
  {
    name: "Shop around list header",
    componentType: "ShopAroundListHeaderBlockComponent",
    category: "catalog",
    description: "Shop search header controls.",
    keywords: ["shop", "search", "header"],
    allowedIn: [{ bloc: "ShopSearchPageComponent", slot: "body" }],
    sharedFields: {},
  },

  () => ({
    title: propertyValue({
      type: "text",
      value: "",
      options: {
        label: "pages.blocks.shop-around-list-header.title.label",
        help: "pages.blocks.shop-around-list-header.title.help",
      },
    }),
    display_title: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.shop-around-list-header.display-title.label",
      },
    }),
    display_address_search_input: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label:
          "pages.blocks.shop-around-list-header.display-address-search-input.label",
        help: "pages.blocks.shop-around-list-header.display-address-search-input.help",
      },
    }),
    shop_search_location_field_background_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "#FFFFFF" },
      options: {
        label:
          "pages.blocks.shop-around-list-header.shop-search-location-field-background-color.label",
      },
    }),
    shop_search_location_field_text_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-900" },
      options: {
        label:
          "pages.blocks.shop-around-list-header.shop-search-location-field-text-color.label",
      },
    }),
    shop_search_location_field_placeholder_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-500" },
      options: {
        label:
          "pages.blocks.shop-around-list-header.shop-search-location-field-placeholder-color.label",
        help: "pages.blocks.shop-around-list-header.shop-search-location-field-placeholder-color.help",
      },
    }),
    shop_search_location_field_icon_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-600" },
      options: {
        label:
          "pages.blocks.shop-around-list-header.shop-search-location-field-icon-color.label",
      },
    }),
    display_filters_button: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label:
          "pages.blocks.shop-around-list-header.display-filters-button.label",
      },
    }),
    display_map_button: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.shop-around-list-header.display-map-button.label",
      },
    }),
    display_map_button_on_mobile: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label:
          "pages.blocks.shop-around-list-header.display-map-button-on-mobile.label",
        help: "pages.blocks.shop-around-list-header.display-map-button-on-mobile.help",
      },
    }),
    shops_map_button_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "primary-100" },
      options: {
        label: "pages.blocks.shop-around-list-header.shops-map-button-color.label",
      },
    }),
    shops_map_button_text_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "#FFFFFF" },
      options: {
        label:
          "pages.blocks.shop-around-list-header.shops-map-button-text-color.label",
      },
    }),
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.shop-around-list-header.tab.content.label",
        fieldsets: [
          {
            legend:
              "pages.blocks.shop-around-list-header.fieldset.title.label",
            fields: [
              "display_title",
              { prop: "title", visibleWhen: { display_title: true } },
            ],
          },
          {
            legend:
              "pages.blocks.shop-around-list-header.fieldset.address-field.label",
            fields: [
              "display_address_search_input",
              {
                prop: "shop_search_location_field_background_color",
                visibleWhen: { display_address_search_input: true },
              },
              {
                prop: "shop_search_location_field_text_color",
                visibleWhen: { display_address_search_input: true },
              },
              {
                prop: "shop_search_location_field_placeholder_color",
                visibleWhen: { display_address_search_input: true },
              },
              {
                prop: "shop_search_location_field_icon_color",
                visibleWhen: { display_address_search_input: true },
              },
            ],
          },
          {
            legend:
              "pages.blocks.shop-around-list-header.fieldset.buttons.label",
            fields: ["display_filters_button"],
          },
          {
            legend:
              "pages.blocks.shop-around-list-header.fieldset.map-button.label",
            fields: [
              "display_map_button",
              {
                prop: "display_map_button_on_mobile",
                visibleWhen: { display_map_button: true },
              },
              {
                prop: "shops_map_button_color",
                visibleWhen: { display_map_button: true },
              },
              {
                prop: "shops_map_button_text_color",
                visibleWhen: { display_map_button: true },
              },
            ],
          },
        ],
      },
    ],
  }),
);

export function shopAroundListHeaderBlockDescriptor(): IBlockDescriptor;
export function shopAroundListHeaderBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function shopAroundListHeaderBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedShopAroundListHeaderBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedShopAroundListHeaderBlockDescriptor,
    shopAroundListHeaderBlockProperties,
    fieldOrder,
  );
}

export {
  shopAroundListHeaderBlockBlocDefinition,
  shopAroundListHeaderBlockDashboardLayout,
  shopAroundListHeaderBlockConfig,
  shopAroundListHeaderBlockResolveConfig,
  shopAroundListHeaderBlockProperties,
};

export type ShopAroundListHeaderBlockConfig = typeof shopAroundListHeaderBlockConfig;
